import { motion } from 'motion/react';
import { X, Check } from 'lucide-react';
import { OnboardingStep } from './OnboardingStepper';

interface PrototypeSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentStep: OnboardingStep;
  onStepChange: (step: OnboardingStep) => void;
}

interface StepOption {
  step: OnboardingStep;
  label: string;
  description: string;
}

const stepOptions: StepOption[] = [
  {
    step: 0,
    label: 'Reverificação necessária',
    description: 'Simular usuário que precisa fazer nova verificação facial.',
  },
  {
    step: 1,
    label: 'Etapa 1 – Validar identidade',
    description: 'Simular usuário que ainda precisa validar a identidade.',
  },
  {
    step: 2,
    label: 'Etapa 2 – Comprar moedas',
    description: 'Simular usuário que precisa comprar moedas.',
  },
  {
    step: 3,
    label: 'Etapa 3 – Completar perfil',
    description: 'Simular usuário que precisa completar o perfil.',
  },
  {
    step: 4,
    label: 'Etapa 4 – Tudo concluído',
    description: 'Simular usuário com tudo liberado.',
  },
];

export function PrototypeSettingsModal({
  isOpen,
  onClose,
  currentStep,
  onStepChange,
}: PrototypeSettingsModalProps) {
  if (!isOpen) return null;

  const handleSelectStep = (step: OnboardingStep) => {
    onStepChange(step);
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <motion.div
        className="fixed inset-0 bg-black z-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto">
        <motion.div
          className="relative w-full max-w-md min-h-full"
          style={{
            backgroundColor: 'var(--background)',
          }}
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        >
          {/* Header */}
          <div
            className="sticky top-0 z-10 flex items-center justify-between px-5 py-4"
            style={{
              backgroundColor: 'var(--background)',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <div>
              <h2
                style={{
                  fontSize: 'var(--text-xl)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--foreground)',
                  lineHeight: '1.4',
                  marginBottom: '4px',
                }}
              >
                Configurações do protótipo
              </h2>
              <p
                style={{
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-normal)',
                  color: 'var(--muted-foreground)',
                  lineHeight: '1.5',
                }}
              >
                Escolha em qual etapa o usuário está para testar a experiência.
              </p>
            </div>
            <button
              onClick={onClose}
              className="flex items-center justify-center transition-colors flex-shrink-0"
              style={{
                width: '32px',
                height: '32px',
                color: 'var(--muted-foreground)',
              }}
              aria-label="Fechar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-5 py-6">
            <div
              className="overflow-hidden"
              style={{
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-card)',
              }}
            >
              {stepOptions.map((option, index) => {
                const isSelected = option.step === currentStep;
                const isLast = index === stepOptions.length - 1;

                return (
                  <motion.button
                    key={option.step}
                    onClick={() => handleSelectStep(option.step)}
                    className="w-full text-left px-4 py-4 transition-colors"
                    style={{
                      backgroundColor: isSelected ? 'var(--kojo-blue-subtle)' : 'var(--background)',
                      borderBottom: isLast ? 'none' : '1px solid var(--border)',
                    }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-start gap-3">
                      {/* Radio indicator */}
                      <div
                        className="flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          border: isSelected
                            ? '6px solid var(--kojo-blue)'
                            : '2px solid var(--border)',
                          backgroundColor: 'var(--background)',
                          transition: 'all 0.2s',
                        }}
                      />

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div
                          style={{
                            fontSize: 'var(--text-base)',
                            fontWeight: isSelected
                              ? 'var(--font-weight-semibold)'
                              : 'var(--font-weight-medium)',
                            color: isSelected ? 'var(--kojo-blue)' : 'var(--foreground)',
                            lineHeight: '1.5',
                            marginBottom: '4px',
                          }}
                        >
                          {option.label}
                        </div>
                        <div
                          style={{
                            fontSize: 'var(--text-sm)',
                            fontWeight: 'var(--font-weight-normal)',
                            color: 'var(--muted-foreground)',
                            lineHeight: '1.5',
                          }}
                        >
                          {option.description}
                        </div>
                      </div>

                      {/* Check icon */}
                      {isSelected && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                          className="flex-shrink-0"
                        >
                          <Check className="w-5 h-5" style={{ color: 'var(--kojo-blue)' }} />
                        </motion.div>
                      )}
                    </div>
                  </motion.button>
                );
              })}
            </div>

            {/* Info footer */}
            <div
              className="mt-4 p-3"
              style={{
                backgroundColor: 'var(--neutral-50)',
                borderRadius: 'var(--radius-button)',
              }}
            >
              <p
                style={{
                  fontSize: 'var(--text-xs)',
                  fontWeight: 'var(--font-weight-normal)',
                  color: 'var(--muted-foreground)',
                  lineHeight: '1.5',
                  textAlign: 'center',
                }}
              >
                Este controle é apenas para testes. As mudanças não são persistidas.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
}